import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { PhoneOff, Plus } from 'lucide-react';
import { leadsAPI } from '../services/api';
import { formatPhone } from '../utils/formatters';
import BrowserCallButton from './BrowserCallButton';

// Every number on a lead (Lead.phones; the primary is also Lead.phone).
// Numbers arrive from forms (a returning person's new number becomes the
// main one, the old one is kept as "Previous"), CSV "Secondary phone" and
// manual edits here. Calls / SMS to any of them are still subject to Do
// Not Call on the server (server/services/contactPolicy.js).

const LABELS = {
  primary: 'Main', mobile: 'Mobile', home: 'Home', work: 'Work', other: 'Other', previous: 'Previous',
};
const ORDER = { primary: 0, mobile: 1, home: 2, work: 3, other: 4, previous: 5 };
const digits10 = (n) => String(n || '').replace(/\D/g, '').slice(-10);

export function leadNumbers(lead) {
  const list = Array.isArray(lead?.phones) && lead.phones.length
    ? lead.phones.filter((p) => p && p.number)
    : (lead?.phone ? [{ number: lead.phone, last10: digits10(lead.phone), label: 'primary' }] : []);
  return [...list].sort((a, b) => (ORDER[a.label] ?? 9) - (ORDER[b.label] ?? 9));
}

const LeadPhoneNumbers = ({ lead, claim }) => {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [number, setNumber] = useState('');
  const [label, setLabel] = useState('mobile');
  const numbers = useMemo(() => leadNumbers(lead), [lead]);
  const dnc = lead?.status === 'do_not_call';

  const refresh = () => queryClient.invalidateQueries(['lead', String(lead._id)]);
  const onError = (err) => toast.error(err.response?.data?.error || 'Could not update phone numbers');

  const addMutation = useMutation(() => leadsAPI.addPhone(lead._id, number.trim(), label), {
    onSuccess: () => { toast.success('Phone number added'); setAdding(false); setNumber(''); refresh(); },
    onError,
  });
  const primaryMutation = useMutation((last10) => leadsAPI.updatePhone(lead._id, last10, { primary: true }), {
    onSuccess: () => { toast.success('Main number updated'); refresh(); },
    onError,
  });
  const removeMutation = useMutation((last10) => leadsAPI.removePhone(lead._id, last10), {
    onSuccess: () => { toast.success('Phone number removed'); refresh(); },
    onError,
  });
  const busy = addMutation.isLoading || primaryMutation.isLoading || removeMutation.isLoading;

  const callLink = (p) => (
    <a
      href={`tel:${p.number}`}
      className={`text-primary-600 hover:text-primary-700 ${claim?.locked ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={(e) => {
        if (!claim || claim.ownerSelf) return;
        e.preventDefault();
        claim.requestClaim(() => { window.location.href = `tel:${p.number}`; });
      }}
    >
      {formatPhone(p.number)}
    </a>
  );

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700">Phone</label>
      <ul className="mt-1 space-y-1.5">
        {numbers.map((p) => {
          const isPrimary = p.label === 'primary';
          return (
            <li key={p.last10 || p.number} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              {dnc ? (
                <span className="text-gray-400 line-through inline-flex items-center gap-2">
                  <PhoneOff size={14} />
                  {formatPhone(p.number)}
                </span>
              ) : callLink(p)}
              {!isPrimary && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${p.label === 'previous' ? 'text-gray-500 border-gray-200 bg-gray-50' : 'text-gray-700 border-gray-200 bg-white'}`}>
                  {LABELS[p.label] || p.label}
                </span>
              )}
              {!dnc && (
                <BrowserCallButton
                  lead={lead}
                  phone={p.number}
                  disabled={claim?.locked}
                  guard={(go) => (!claim || claim.ownerSelf ? go() : claim.requestClaim(go))}
                />
              )}
              {!isPrimary && (
                <span className="inline-flex items-center gap-2 text-xs">
                  <button type="button" disabled={busy} onClick={() => primaryMutation.mutate(p.last10)} className="text-primary-600 hover:underline disabled:opacity-50">
                    Make main
                  </button>
                  <button type="button" disabled={busy} onClick={() => removeMutation.mutate(p.last10)} className="text-gray-500 hover:text-red-600 hover:underline disabled:opacity-50">
                    Remove
                  </button>
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {adding ? (
        <form
          className="mt-2 flex flex-wrap items-center gap-2"
          onSubmit={(e) => { e.preventDefault(); if (number.trim()) addMutation.mutate(); }}
        >
          <input
            type="tel"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="(555) 123-4567"
            autoFocus
            className="input flex-1 min-w-[10rem] text-sm py-1.5"
          />
          <select value={label} onChange={(e) => setLabel(e.target.value)} className="input w-auto text-sm py-1.5">
            {['mobile', 'home', 'work', 'other'].map((l) => <option key={l} value={l}>{LABELS[l]}</option>)}
          </select>
          <button type="submit" disabled={busy || !number.trim()} className="btn-primary text-xs py-1.5 px-3">Add</button>
          <button type="button" onClick={() => { setAdding(false); setNumber(''); }} className="btn-secondary text-xs py-1.5 px-3">Cancel</button>
        </form>
      ) : (
        <button type="button" onClick={() => setAdding(true)} className="mt-1.5 inline-flex items-center gap-1 text-xs text-primary-600 hover:underline">
          <Plus size={12} /> Add number
        </button>
      )}
    </div>
  );
};

export default LeadPhoneNumbers;
